import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams } from 'react-router-dom';
import { FaCalendarAlt, FaUser, FaTag, FaShare, FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { getPostById, getRelatedPosts, getCategoryName, getRecentPosts, blogPosts } from '../data/blogData';
import { useApiData } from '../hooks/useApiData';
import { fetchBlogById, fetchBlogs, fetchImage } from '../repository/api';
import { formatExcerpt, formatBlogContent } from '../utils/textFormatter';
import LoadingSpinner from '../components/LoadingSpinner';

const BlogPostPage = ({ match }) => {
  // Get the post ID from the URL parameters
  const { postId } = useParams();
  
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  
  // Fetch blog post from API
  const { data: blogData, loading: blogLoading, error: blogError } = useApiData(() => 
    fetchBlogById(postId), [postId]
  );
  
  // Fetch recent posts for sidebar
  const { data: recentBlogsData, loading: recentLoading, error: recentError } = useApiData(() => 
    fetchBlogs({ per_page: 10, sort_by: 'created_at', sort_order: 'desc' })
  );

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) console.log('BlogPost API Data received:', apiData);
    
    if (!apiData) {
      return null;
    }
    
    return {
      id: apiData.id,
      title: apiData.title || 'Untitled Blog Post',
      excerpt: formatExcerpt(apiData.paragraph_explanation || 'No description available'),
      category: apiData.category?.toLowerCase() || 'general',
      date: new Date(apiData.created_at).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      author: apiData.author_name || 'Anonymous',
      authorImage: null,
      authorBio: '',
      image: apiData.image_path ? fetchImage(apiData.image_path) : blogPosts[0].image,
      featured: false,
      tags: apiData.tags || [],
      content: apiData.sections ? formatBlogContent(apiData.sections) : ''
    };
  };

  // Transform recent posts data
  const transformRecentPostsData = (apiData) => {
    if (!apiData || !apiData.blogs || !Array.isArray(apiData.blogs)) {
      return [];
    }
    
    return apiData.blogs
      .filter(blog => blog.id !== postId) // Exclude current post
      .slice(0, 4)
      .map(blog => transformApiData(blog));
  };

  // Use API data or fallback to default
  const post = useMemo(() => {
    if (blogData && !blogLoading && !blogError) {
      return transformApiData(blogData);
    }
    
    // Fallback to default data
    if (postId && !isNaN(postId)) {
      return getPostById(postId);
    }
    
    return null;
  }, [blogData, blogLoading, blogError, postId]);

  const recentPostsFromApi = useMemo(() => {
    if (recentBlogsData && !recentLoading && !recentError) {
      return transformRecentPostsData(recentBlogsData);
    }
    return [];
  }, [recentBlogsData, recentLoading, recentError, postId]);

  // Set related and recent posts
  useEffect(() => {
    if (post) {
      // Get related posts from default data (since API doesn't have related posts endpoint)
      setRelatedPosts(getRelatedPosts(post.id, post.category));
      
      // Use API recent posts if available, otherwise fallback to default
      if (recentPostsFromApi.length > 0) {
        setRecentPosts(recentPostsFromApi);
      } else {
        const availablePosts = blogPosts.filter(p => p.id !== parseInt(post.id));
        const shuffled = availablePosts.sort(() => 0.5 - Math.random());
        setRecentPosts(shuffled.slice(0, 4));
      }
    }
  }, [post, recentPostsFromApi]);

  // Debug logging (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('BlogPostPage component state:', {
        postId,
        blogLoading,
        blogError,
        recentLoading,
        recentError,
        hasPost: !!post,
        hasRelatedPosts: relatedPosts.length > 0,
        hasRecentPosts: recentPosts.length > 0,
        blogData,
        recentBlogsData
      });
    }
  }, [postId, blogLoading, blogError, recentLoading, recentError, post, relatedPosts.length, recentPosts.length, blogData, recentBlogsData]);

  // Show error state if API call failed (but continue with fallback data)
  if (blogError || recentError) {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) {
      console.error('BlogPost page API errors:', { blogError, recentError });
      console.log('Using fallback blog data due to API errors');
    }
  }

  if (blogLoading) {
    return <LoadingWrapper>
      <LoadingSpinner 
        size="large" 
        text="Loading article..." 
        overlay={false}
        showLogo={true}
      />
    </LoadingWrapper>;
  }

  if (!post) {
    return <ErrorMessage>The article could not be found.</ErrorMessage>;
  }

  return (
    <PageWrapper>
      <Container>
        <BreadcrumbNav>
          <BreadcrumbLink href="/">Home</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink href="/blog">Blog</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbCurrent>{post.title}</BreadcrumbCurrent>
        </BreadcrumbNav>
        
        <ArticleWrapper>
          <MainContent>
            <ArticleHeader>
              <CategoryBadge>{getCategoryName(post.category)}</CategoryBadge>
              <ArticleTitle>{post.title}</ArticleTitle>
              
              <ArticleMeta>
                <MetaItem>
                  <FaCalendarAlt />
                  <span>{post.date}</span>
                </MetaItem>
                <MetaItem>
                  <FaUser />
                  <span>{post.author}</span>
                </MetaItem>
                <MetaItem>
                  <FaTag />
                  <span>{getCategoryName(post.category)}</span>
                </MetaItem>
              </ArticleMeta>
            </ArticleHeader>
            
            <FeaturedImage src={post.image} alt={post.title} />
            
            <ArticleContent dangerouslySetInnerHTML={{ __html: post.content }} />
            
            <TagsSection>
              <TagsTitle>Tags:</TagsTitle>
              {post.tags.map(tag => (
                <TagBadge key={tag} href={`/blog/tag/${tag}`}>{tag}</TagBadge>
              ))}
            </TagsSection>
            
            <ShareSection>
              <ShareTitle>Share this article:</ShareTitle>
              <ShareButtons>
                <ShareButton color="#3b5998">
                  <FaFacebook />
                </ShareButton>
                <ShareButton color="#1da1f2">
                  <FaTwitter />
                </ShareButton>
                <ShareButton color="#0077b5">
                  <FaLinkedin />
                </ShareButton>
                <ShareButton color="#25d366">
                  <FaWhatsapp />
                </ShareButton>
              </ShareButtons>
            </ShareSection>
            
            <AuthorSection>
              <AuthorImage src={post.authorImage} alt={post.author} />
              <AuthorDetails>
                <AuthorName>{post.author}</AuthorName>
                <AuthorBio>{post.authorBio}</AuthorBio>
              </AuthorDetails>
            </AuthorSection>
          </MainContent>
          
          <Sidebar>
            <SidebarWidget>
              <WidgetTitle>Recent Posts</WidgetTitle>
              <RecentPostsList>
                {recentPosts.map(rPost => (
                  <RecentPostItem key={rPost.id}>
                    <RecentPostImage src={rPost.image} alt={rPost.title} />
                    <RecentPostContent>
                      <RecentPostTitle href={`/blog/${rPost.id}`}>{rPost.title}</RecentPostTitle>
                      <RecentPostDate>{rPost.date}</RecentPostDate>
                    </RecentPostContent>
                  </RecentPostItem>
                ))}
              </RecentPostsList>
            </SidebarWidget>
            
            <SidebarWidget>
              <WidgetTitle>Categories</WidgetTitle>
              <CategoryList>
                <CategoryItem><CategoryLink href="/blog/category/sms">Bulk SMS</CategoryLink></CategoryItem>
                <CategoryItem><CategoryLink href="/blog/category/ussd">USSD Services</CategoryLink></CategoryItem>
                <CategoryItem><CategoryLink href="/blog/category/call-center">Call Center</CategoryLink></CategoryItem>
                <CategoryItem><CategoryLink href="/blog/category/payments">Payments</CategoryLink></CategoryItem>
                <CategoryItem><CategoryLink href="/blog/category/software">Software Dev</CategoryLink></CategoryItem>
              </CategoryList>
            </SidebarWidget>
            
            <SidebarWidget>
              <WidgetTitle>Subscribe</WidgetTitle>
              <SubscribeForm>
                <p>Get the latest articles directly to your inbox</p>
                <SubscribeInput type="email" placeholder="Your email address" />
                <SubscribeButton>Subscribe</SubscribeButton>
              </SubscribeForm>
            </SidebarWidget>
          </Sidebar>
        </ArticleWrapper>
        
        <RelatedPostsSection>
          <SectionTitle>Related Articles</SectionTitle>
          <RelatedPostsGrid>
            {relatedPosts.slice(0, 3).map(relatedPost => (
              <RelatedPostCard key={relatedPost.id}>
                <RelatedPostImage src={relatedPost.image} alt={relatedPost.title} />
                <RelatedPostCategory>{getCategoryName(relatedPost.category)}</RelatedPostCategory>
                <RelatedPostContent>
                  <RelatedPostTitle href={`/blog/${relatedPost.id}`}>{relatedPost.title}</RelatedPostTitle>
                  <RelatedPostMeta>
                    <MetaItem>
                      <FaCalendarAlt />
                      <span>{relatedPost.date}</span>
                    </MetaItem>
                  </RelatedPostMeta>
                  <RelatedPostExcerpt>{relatedPost.excerpt}</RelatedPostExcerpt>
                </RelatedPostContent>
              </RelatedPostCard>
            ))}
          </RelatedPostsGrid>
        </RelatedPostsSection>
        
        <BlogNavigation>
          <NavLink href="/blog/previous-post">
            <FaArrowLeft /> Previous Article
          </NavLink>
          <NavLink href="/blog" center>
            All Articles
          </NavLink>
          <NavLink href="/blog/next-post" isNext>
            Next Article <FaArrowRight />
          </NavLink>
        </BlogNavigation>
      </Container>
    </PageWrapper>
  );
};

export default BlogPostPage;

// === Animations ===
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// === Styled Components ===
const PageWrapper = styled.div`
  background-color: #f8f9fb;
  margin-top: 10vh;
  padding: 50px 0 80px;
  
  @media (max-width: 768px) {
    padding: 30px 0 60px;
  }
`;

const Container = styled.div`

  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
`;

const BreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  font-size: 14px;
  flex-wrap: wrap;
`;

const BreadcrumbLink = styled.a`
  color: #666;
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: #2090B0;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 10px;
  color: #ccc;
`;

const BreadcrumbCurrent = styled.span`
  color: #2090B0;
  font-weight: 500;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArticleWrapper = styled.div`
  display: flex;
  gap: 40px;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const MainContent = styled.article`
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const Sidebar = styled.aside`
  width: 340px;
  
  @media (max-width: 992px) {
    width: 100%;
  }
`;

const ArticleHeader = styled.header`
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 576px) {
    padding: 25px 20px;
  }
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 15px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ArticleTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #105090;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 30px;
  }
  
  @media (max-width: 576px) {
    font-size: 24px;
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: #777;
  font-size: 14px;
  
  svg {
    margin-right: 6px;
    color: #2090B0;
  }
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
`;

const ArticleContent = styled.div`
  padding: 40px;
  color: #444;
  line-height: 1.7;
  font-size: 17px;
  
  h2 {
    margin-top: 40px;
    margin-bottom: 20px;
    font-size: 26px;
    font-weight: 700;
    color: #105090;
  }
  
  h3 {
    margin-top: 30px;
    margin-bottom: 15px;
    font-size: 22px;
    font-weight: 600;
    color: #105090;
  }
  
  p {
    margin-bottom: 20px;
  }
  
  ul, ol {
    margin-bottom: 20px;
    padding-left: 25px;
  }
  
  li {
    margin-bottom: 10px;
  }
  
  a {
    color: #2090B0;
    text-decoration: none;
    transition: all 0.3s ease;
    
    &:hover {
      color: #105090;
      text-decoration: underline;
    }
  }
  
  blockquote {
    margin: 30px 0;
    padding: 20px 30px;
    background-color: #f8f9fb;
    border-left: 4px solid #2090B0;
    font-style: italic;
    font-size: 18px;
    color: #555;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 30px 0;
  }
  
  @media (max-width: 768px) {
    padding: 30px;
    font-size: 16px;
    
    h2 {
      font-size: 24px;
    }
    
    h3 {
      font-size: 20px;
    }
  }
  
  @media (max-width: 576px) {
    padding: 25px 20px;
  }
`;

const TagsSection = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 40px 20px;
  gap: 10px;
  
  @media (max-width: 768px) {
    padding: 0 30px 20px;
  }
  
  @media (max-width: 576px) {
    padding: 0 20px 20px;
  }
`;

const TagsTitle = styled.span`
  color: #666;
  font-weight: 600;
  margin-right: 5px;
`;

const TagBadge = styled.a`
  background-color: #f0f2f5;
  color: #666;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #e0e2e5;
    color: #2090B0;
  }
`;

const ShareSection = styled.section`
  display: flex;
  align-items: center;
  padding: 20px 40px;
  border-top: 1px solid #f0f2f5;
  
  @media (max-width: 768px) {
    padding: 20px 30px;
    flex-wrap: wrap;
    gap: 10px;
  }
  
  @media (max-width: 576px) {
    padding: 20px;
  }
`;

const ShareTitle = styled.span`
  color: #666;
  font-weight: 600;
  margin-right: 15px;
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ShareButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${props => props.color || '#333'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 16px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    opacity: 0.9;
  }
`;

const AuthorSection = styled.section`
  display: flex;
  padding: 30px 40px;
  background-color: #f8f9fb;
  border-top: 1px solid #f0f2f5;
  
  @media (max-width: 768px) {
    padding: 25px 30px;
  }
  
  @media (max-width: 576px) {
    padding: 20px;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 15px;
  }
`;

const AuthorImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 20px;
  border: 3px solid white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 576px) {
    margin-right: 0;
  }
`;

const AuthorDetails = styled.div`
  flex: 1;
`;

const AuthorName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 10px;
  color: #105090;
`;

const AuthorBio = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

// Sidebar components
const SidebarWidget = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
  overflow: hidden;
`;

const WidgetTitle = styled.h3`
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  font-size: 18px;
  font-weight: 600;
  padding: 15px 20px;
  margin: 0;
`;

// Recent Posts Widget
const RecentPostsList = styled.div`
  padding: 20px;
`;

const RecentPostItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f2f5;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const RecentPostImage = styled.img`
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 15px;
`;

const RecentPostContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RecentPostTitle = styled.a`
  font-size: 14px;
  font-weight: 600;
  color: #105090;
  text-decoration: none;
  margin-bottom: 5px;
  line-height: 1.4;
  transition: all 0.3s ease;
  
  &:hover {
    color: #2090B0;
  }
`;

const RecentPostDate = styled.span`
  font-size: 12px;
  color: #999;
`;

// Categories Widget
const CategoryList = styled.ul`
  list-style: none;
  padding: 20px;
  margin: 0;
`;

const CategoryItem = styled.li`
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f2f5;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const CategoryLink = styled.a`
  color: #444;
  text-decoration: none;
  font-size: 15px;
  display: flex;
  justify-content: space-between;
  transition: all 0.3s ease;
  
  &:hover {
    color: #2090B0;
    padding-left: 5px;
  }
  
  &::after {
    content: '→';
    font-size: 18px;
    opacity: 0;
    transition: all 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

// Subscribe Widget
const SubscribeForm = styled.form`
  padding: 20px;
  
  p {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 14px;
    color: #666;
  }
`;

const SubscribeInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #2090B0;
    box-shadow: 0 0 0 3px rgba(32, 144, 176, 0.1);
  }
`;

const SubscribeButton = styled.button`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;
  
  &:hover {
    background-position: right center;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

// Related Posts Section
const RelatedPostsSection = styled.section`
  margin-top: 60px;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 40px;
  color: #105090;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #105090, #2090B0);
    border-radius: 3px;
  }
`;

const RelatedPostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedPostCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
`;

const RelatedPostImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  position: relative;
`;

const RelatedPostCategory = styled.span`
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transform: translateY(-165px);
`;

const RelatedPostContent = styled.div`
  padding: 25px;
`;

const RelatedPostTitle = styled.a`
  display: block;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  line-height: 1.4;
  color: #105090;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: #2090B0;
  }
  
  @media (max-width: 576px) {
    font-size: 16px;
  }
`;

const RelatedPostMeta = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const RelatedPostExcerpt = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Blog Navigation
const BlogNavigation = styled.nav`
  display: flex;
  justify-content: space-between;
  margin-top: 60px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  padding: 12px 25px;
  background: white;
  border-radius: 8px;
  text-decoration: none;
  color: #105090;
  font-weight: 600;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  ${props => props.center && 'justify-content: center;'}
  ${props => props.isNext && 'justify-content: flex-end;'}
  
  svg {
    ${props => !props.isNext ? 'margin-right: 8px;' : 'margin-left: 8px;'}
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: #2090B0;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    
    svg {
      transform: ${props => !props.isNext ? 'translateX(-3px)' : 'translateX(3px)'};
    }
  }
  
  @media (max-width: 576px) {
    justify-content: center;
  }
`;

// Loading components
const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 16px;
`;

// Error message
const ErrorMessage = styled.div`
  text-align: center;
  padding: 100px 0;
  color: #666;
  font-size: 18px;
`;